import capitalize from 'lodash/capitalize';
import pdf from 'vue-pdf';
import { progressList } from '@/constants/variables';
import { TEST_FIRM_IDS } from '@/constants/testing';
import API from '@/api';
import uniq from 'lodash/uniq';
import heicConvert from 'heic-convert';
import { getCookie, setCookie } from '../utils/cookies';

const REDACTED_CLASS = 'redacted';
const REDACTED_COOKIE = 'SORABAN_REDACTED';

const EXPORT_BANNER_COOKIE = 'EXPORT_BANNER_COOKIE';

export default {
  methods: {
    heicConvert,
    async asyncForEach(array = [], callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },
    numberWithCommas(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },
    truncateString(str, num) {
      if (str.length <= num) {
        return str;
      }
      return str.slice(0, num) + '...';
    },
    saveAs(uri, filename) {
      let link = document.createElement('a');
      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;
        // Firefox requires the link to be in the body
        document.body.appendChild(link);
        // simulate click
        link.click();
        // remove the link when done
        document.body.removeChild(link);
      } else {
        window.open(uri, '_blank');
      }
    },
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    setDocumentDirectionPerLocale(locale) {
      document.dir = locale === 'he' ? 'rtl' : 'ltr';
      if (locale === 'he') {
        document.querySelector('body').classList.toggle('rtl');
      }
    },
    setDocumentLang(lang) {
      document.documentElement.lang = lang;
    },
    copyTextToClipboard(text) {
      if (!navigator.clipboard) {
        this.fallbackCopyTextToClipboard(text);
        return;
      }

      navigator.clipboard.writeText(text);
    },

    fallbackCopyTextToClipboard(text) {
      var textArea = document.createElement('textarea');
      textArea.value = text;

      // Avoid scrolling to bottom
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        var successful = document.execCommand('copy');
        var msg = successful ? 'successful' : 'unsuccessful';
      } catch (err) {
        console.log(err);
      }

      document.body.removeChild(textArea);
    },
    formatPhoneNumber(phoneNumberString) {
      const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
      }
      return phoneNumberString;
    },
    maskPhoneNumber(number) {
      let USNumber = number.match(/(\d{3})(\d{3})(\d{4})/);
      if (USNumber) {
        return '(' + USNumber[1] + ') ' + USNumber[2] + '-' + USNumber[3];
      } else {
        return number;
      }
    },
    // Capitalizes a string
    capitalizeWords(string) {
      const isValidString = !!string && typeof string === 'string';

      // Skip logic if it isn't a string or empty
      if (!isValidString) return '';

      const capitalizeString = string => capitalize(string);

      // Account for spaces and hyphen in strings
      return string
        .split('-')
        .map(capitalizeString)
        .join('-')
        .split(' ')
        .map(capitalizeString)
        .join(' ');
    },

    pdfHasPassword(data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = async () => {
          const loadingTask = await pdf.createLoadingTask(
            new Uint8Array(reader.result),
          );

          loadingTask.onPassword = (_, reason) => {
            // NEED PASSWORD
            if (reason === 1) {
              resolve(true);
            }
          };

          loadingTask.promise.then(() => {
            resolve(false);
          });
        };

        reader.onerror = event => {
          reject(event);
        };

        reader.readAsArrayBuffer(data);
      });
    },

    getExtensionOfFilename(filename) {
      return filename.split('.').pop();
    },

    getTrueExtension(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          const arr = new Uint8Array(reader.result).subarray(0, 4);
          let header = '';

          for (let i = 0; i < arr.length; i++) {
            header += arr[i].toString(16);
          }

          switch (header) {
            case '89504e47':
              resolve('png');
            case '47494638':
              resolve('gif');
            case '25504446':
              resolve('pdf');
            case 'ffd8ffe0':
            case 'ffd8ffe1':
            case 'ffd8ffe2':
              resolve('jpeg');
            default:
              resolve('unknown');
          }
        };

        reader.readAsArrayBuffer(file);
      });
    },
    // NOTE @chris Doing this manually is unsafe, but loading the latest versions of the popular sanitizer libraries needs node upgrade.
    removeHtml(input) {
      return input.replace(/<\/?[^>]+>/gi, '');
    },

    limitString(text = '', limit = 240) {
      if (!text || !limit) return '';

      const isShorter = text.length <= limit;

      return isShorter ? text : text.substring(0, limit) + '\u2026';
    },

    getDocumentName(path) {
      return path.split('/')[path.split('/').length - 1];
    },

    async downloadDocumentByLink(url, name) {
      const link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', name);
      link.target = '_blank';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    async downloadDocumentAsBuffer(companyId, documentLink) {
      const response = await API.integration.downloadDocument({
        companyId: companyId,
        link: documentLink,
      });

      const url = response.data.document.data.link;

      return fetch(url).then(res => res.arrayBuffer());
    },

    convertToRanges(array) {
      const uniqSorted = uniq(array.sort((a, b) => a - b));
      const ranges = [];

      for (let i = 0; i < uniqSorted.length; i++) {
        let rangeStart = uniqSorted[i];
        let rangeEnd = uniqSorted[i];

        while (uniqSorted[i + 1] - uniqSorted[i] === 1) {
          rangeEnd = uniqSorted[i + 1];
          i++;
        }

        if (rangeStart === rangeEnd) ranges.push(rangeStart);
        else ranges.push(`${rangeStart}-${rangeEnd}`);
      }

      return ranges;
    },

    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });

      return formatter.format(amount);
    },
    getIsSorabanAdvisorAdmin({ email = '', is_advisor = false }) {
      if (!email || !is_advisor) return false;

      const isSorabanAdmin = Boolean(
        email &&
          is_advisor &&
          email.startsWith('hello+') &&
          email.endsWith('@soraban.com'),
      );

      return isSorabanAdmin;
    },
    // NOTE @chris Toggles the .redacted on body to toggle the [Redacted] label to hide PII data. This is discouraged, but sometimes necessary for the support team.
    toggleRedacted(user) {
      const isSorabanAdmin = this.getIsSorabanAdvisorAdmin(user);

      // Do nothing for non Soraban Admin user
      if (!isSorabanAdmin) return;

      // but offer toggle for Soraban Admins
      const hasRedacted = document.body.classList.contains(REDACTED_CLASS);

      if (hasRedacted && confirm('This will show PII data. Are you sure?')) {
        setCookie(REDACTED_COOKIE, 'true', 31);
        return document.body.classList.remove(REDACTED_CLASS);
      } else {
        setCookie(REDACTED_COOKIE, 'false', 31);
        return document.body.classList.add(REDACTED_CLASS);
      }
    },
    dismissExportBanner() {
      setCookie(EXPORT_BANNER_COOKIE, true);

      this.exportBannerDismissed = true;
      this.$forceUpdate();
    },
    addRedacted(user, firm) {
      const isSorabanAdmin = this.getIsSorabanAdvisorAdmin(user);
      const isTestFirm = TEST_FIRM_IDS.includes(firm ? firm.id : -1);

      const disabledCookie = getCookie(REDACTED_COOKIE);
      const hasDisabledCookie = Boolean(
        disabledCookie && disabledCookie === 'true',
      );

      if (!isSorabanAdmin || !isTestFirm || hasDisabledCookie) return;

      return document.body.classList.add(REDACTED_CLASS);
    },
    getUserWithHashId(company) {
      if (!company) return '';

      const hash = company
        ? company.name
            .replaceAll(' ', '-')
            .replaceAll(',', '')
            .replaceAll('#', '') +
          '-' +
          company.hash_id
        : null;

      return hash;
    },
  },
  computed: {
    rightToLeft() {
      return document.dir === 'rtl';
    },
    hasExportBanner() {
      const exportBanner = getCookie(EXPORT_BANNER_COOKIE);

      return !exportBanner;
    },
    data() {
      return {
        exportBannerDismissed: false,
      };
    },
  },
};
